import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Bar } from "react-chartjs-2";
import { Loader2, BarChart3 } from "lucide-react";

import { getAllUnits } from "../../redux/unitSlice/unitSlice";
import CustomSelectWithFilter from "../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../components/Common/CustomSelect/CustomMultiSelect";
import {
    getProjectWiseCostChartData,
    getProjectWiseCostChartOptions,
    getProjectWiseCostChartHeight,
    getProjectWiseCostXAxisTicks,
    PROJECT_WISE_COST_RIGHT_PADDING_PX,
    projectWiseCostValueLabelPlugin,
} from "./chartConfigs/projectWiseCostChart";
import { COST_TYPE_OPTIONS, VALUE_MODE_OPTIONS, getChartFilterSummary, formatUnitValue } from "./unitWiseCostFilterOptions";
import { makeProjectKey } from "./useUnitWiseCostData";
import { useProjectWiseCostData } from "./useProjectWiseCostData";
import ChartFilterBadges from "./ChartFilterBadges";

const MAX_CHART_HEIGHT_PX = 600;

/**
 * "In a Unit, which projects reflect the highest cost" - horizontal bar,
 * ranked by each project's highest-contributing unit (or, if a specific
 * unit is selected, that project's value in just that unit).
 *
 * Fully independent of the other charts: own filters, own fetch.
 *
 * @param {object[]} masterProjects - shared project list (fetched once by parent)
 */
const ProjectWiseCostBarChart = ({ masterProjects = [] }) => {

    const dispatch = useDispatch();

    const [selectedFY, setSelectedFY] = useState("OVERALL");
    const [costLevel, setCostLevel] = useState("DIRECT_MATERIAL");
    const [valueMode, setValueMode] = useState("PERCENT");
    const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState("ALL");

    const { allUnitsList } = useSelector((state) => state.unit);

    useEffect(() => {
        dispatch(getAllUnits());
    }, [dispatch]);

    const unitOptions = useMemo(() => {
        const list = allUnitsList?.data?.data ?? allUnitsList?.data ?? allUnitsList ?? [];
        const options = list.map((u) => ({
            label: u.UNIT_DISPLAY_NAME,
            value: u.MNF_UNIT,
        }));
        return [{ label: "All", value: "ALL" }, ...options];
    }, [allUnitsList]);

    const projectKeyToProject = useMemo(() => {
        const map = new Map();
        masterProjects.forEach((project) => {
            map.set(makeProjectKey(project.IO_NO, project.REV_NO), project);
        });
        return map;
    }, [masterProjects]);

    const {
        loading,
        rankedRows,
        financialYearOptions,
    } = useProjectWiseCostData({
        costLevel,
        valueMode,
        selectedFY,
        selectedProjectKeys,
        projectKeyToProject,
        selectedUnit,
    });

    const chartData = useMemo(
        () => getProjectWiseCostChartData(rankedRows, valueMode),
        [rankedRows, valueMode]
    );

    const chartOptions = useMemo(
        () => getProjectWiseCostChartOptions(valueMode, rankedRows),
        [valueMode, rankedRows]
    );

    const axisTicks = useMemo(
        () => getProjectWiseCostXAxisTicks(rankedRows),
        [rankedRows]
    );

    const chartHeight = getProjectWiseCostChartHeight(rankedRows.length);

    const filterSummary = useMemo(
        () => getChartFilterSummary({
            costLevel,
            selectedFY,
            valueMode,
            selectedProjectKeys,
            projectKeyToProject,
            selectedUnit,
            unitOptions,
        }),
        [costLevel, selectedFY, valueMode, selectedProjectKeys, projectKeyToProject, selectedUnit, unitOptions]
    );

    const costTypeHeaderLabel = costLevel === "TOTAL_COST"
        ? "Factory Cost Level"
        : `${filterSummary.costTypeLabel} Cost`;

    return (

        <div className="bg-white border border-[#EAECF0] rounded-xl p-5">

            {/* FILTERS */}

            <div
                className="
                    flex
                    flex-wrap
                    items-start
                    gap-4
                    p-4
                    mb-6
                    bg-[#F9FAFB]
                    border
                    border-[#EAECF0]
                    rounded-xl
                "
            >

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Financial Year</span>
                    <CustomSelectWithFilter
                        data={financialYearOptions}
                        value={selectedFY}
                        onChange={(value) => setSelectedFY(value || "OVERALL")}
                        placeholder="Overall"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Cost Type</span>
                    <CustomSelectWithFilter
                        data={COST_TYPE_OPTIONS}
                        value={costLevel}
                        onChange={setCostLevel}
                        placeholder="Cost Type"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Value Mode</span>
                    <CustomSelectWithFilter
                        data={VALUE_MODE_OPTIONS}
                        value={valueMode}
                        onChange={setValueMode}
                        placeholder="Value Mode"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Projects Filter</span>
                    <CustomMultiSelect
                        data={masterProjects.map((project) => ({
                            label: project.PROJ_NAME,
                            value: makeProjectKey(project.IO_NO, project.REV_NO),
                        }))}
                        value={selectedProjectKeys}
                        onChange={setSelectedProjectKeys}
                        placeholder="All Projects"
                        className="w-[260px]"
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Unit</span>
                    <CustomSelectWithFilter
                        data={unitOptions}
                        value={selectedUnit}
                        onChange={(value) => setSelectedUnit(value || "ALL")}
                        placeholder="Unit"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>

            </div>

            {/* HEADER */}

            <div className="flex items-center gap-2 mb-6">
                <BarChart3 size={20} className="text-[#155EEF]" />
                <div>
                    <div className="text-xl font-semibold text-[#101828]">
                        {`${costTypeHeaderLabel} - ${
                            selectedUnit === "ALL" ? "All Units" : filterSummary.unitLabel ?? selectedUnit
                        } - Projects`}
                    </div>
                    <ChartFilterBadges
                        fyLabel={filterSummary.fyLabel}
                        valueMode={valueMode}
                        valueModeLabel={filterSummary.valueModeLabel}
                        projectNames={filterSummary.projectNames}
                        unitLabel={filterSummary.unitLabel}
                    />
                </div>
            </div>

            {/* CONTENT */}

            {loading ? (
                <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-[#155EEF]" />
                </div>
            ) : rankedRows.length === 0 ? (
                <div className="flex items-center justify-center h-[400px] text-sm text-[#667085]">
                    No data available
                </div>
            ) : (
                <div
                    style={{
                        height: `${Math.min(chartHeight, MAX_CHART_HEIGHT_PX)}px`,
                        overflow: "hidden",
                        overflowY: chartHeight > MAX_CHART_HEIGHT_PX ? "auto" : "visible",
                    }}
                >
                    {/* Sticky axis: stays pinned to the top of the scroll
                        viewport so the percentage/crore scale is always
                        readable, no matter how far the bars below are
                        scrolled. Plain CSS, not a second chart - its labels
                        share the body chart's stepSize/right-padding so they
                        line up with the gridlines drawn underneath. No gap
                        below the border - the canvas (and its gridlines)
                        starts right where it ends, and the header's own
                        opaque background is what keeps scrolled-up bars
                        from showing through while it's pinned. */}
                    <div
                        style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            paddingRight: `${PROJECT_WISE_COST_RIGHT_PADDING_PX}px`,
                            paddingBottom: "4px",
                            borderBottom: "1px solid #98A2B3",
                            backgroundColor: "#fff",
                        }}
                    >
                        {axisTicks.map((tick, index) => (
                            <span
                                key={index}
                                className="text-xs font-semibold text-[#475467]"
                            >
                                {formatUnitValue(tick, valueMode)}
                            </span>
                        ))}
                    </div>
                    <div style={{ height: `${chartHeight}px` }}>
                        <Bar
                            key={selectedUnit}
                            data={chartData}
                            options={chartOptions}
                            plugins={[projectWiseCostValueLabelPlugin]}
                        />
                    </div>
                </div>
            )}

        </div>

    );

};

export default ProjectWiseCostBarChart;